import { motion } from 'framer-motion'
import { Check, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { services, seoMeta } from '../data/pages'
import { useScrollAnimation } from '../hooks/useScrollAnimation'
import { getIcon } from '../components/IconMap'
import SEO from '../components/SEO'

export default function ServicesPage() {
  const { ref, isVisible } = useScrollAnimation()

  return (
    <>
      <SEO title={seoMeta.services.title} description={seoMeta.services.description} />

      <section className="relative bg-earth-900 pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blossom-950 via-earth-900 to-prime-950" />
        <div className="container-wide section-padding relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge-green mb-4">Services</span>
            <h1 className="text-display-1 font-bold text-white mb-4">Our Services</h1>
            <p className="text-earth-300 text-lg max-w-2xl">From a single sparkle clean to ongoing contract cleaning across multiple sites, we deliver consistent, high-standard results — on time, to specification and with minimal disruption to your operation.</p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding-y section-padding bg-earth-50" ref={ref}>
        <div className="container-wide space-y-24">
          {services.map((service, index) => {
            const Icon = getIcon(service.icon)
            const iconBg = service.color === 'blossom' ? 'bg-blossom-100 text-blossom-600' : service.color === 'prime' ? 'bg-prime-100 text-prime-600' : 'bg-accent-100 text-accent-600'
            return (
            <motion.div key={service.id} initial={{ opacity: 0, y: 40 }} animate={isVisible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${iconBg}`}>
                  <Icon className="w-8 h-8" />
                </div>
                <h2 className="text-3xl font-bold text-earth-900 mb-4">{service.title}</h2>
                <p className="text-earth-500 text-lg leading-relaxed mb-6">{service.description}</p>
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-blossom-100 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3 h-3 text-blossom-600" /></div>
                      <span className="text-earth-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="btn-primary">Request This Service<ArrowRight className="w-5 h-5" /></Link>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-blossom-500/10 to-prime-500/10 rounded-3xl blur-2xl" />
                  <div className="relative aspect-[4/3] bg-gradient-to-br from-earth-200 to-earth-300 rounded-3xl overflow-hidden">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                </div>
              </div>
            </motion.div>
          )
          })}
        </div>
      </section>
    </>
  )
}
